import { Button } from '@/components/ui/button';
import { useRegistrationStore } from '@/lib/store/registrationStore';
import { cn } from '@/lib/utils';
import { Check } from 'lucide-react';
import { usePageTranslation } from '@/i18n/useTranslation';
import { authTranslations } from '@/i18n/locales/en/auth';
import { authTranslations as thAuthTranslations } from '@/i18n/locales/th/auth';

interface StepCrmTeamSizeProps {
  onNext: () => void;
}

const TEAM_SIZE_OPTIONS = [
  { id: 'solo', icon: '👤', labelKey: 'crmTeamSolo' as const },
  { id: '2_5', icon: '👥', labelKey: 'crmTeam2to5' as const },
  { id: '6_20', icon: '👨‍👩‍👧‍👦', labelKey: 'crmTeam6to20' as const },
  { id: 'over_20', icon: '🏢', labelKey: 'crmTeamOver20' as const },
];

export function StepCrmTeamSize({ onNext }: StepCrmTeamSizeProps) {
  const { qualificationData, updateQualificationData } = useRegistrationStore();
  const t = usePageTranslation({ en: authTranslations, th: thAuthTranslations });

  const selected = qualificationData.crmTeamSize || null;

  const handleSelect = (id: string) => {
    updateQualificationData({ crmTeamSize: selected === id ? null : id });
  };

  return (
    <div className="flex flex-col">
      <div className="mb-4">
        <h2 className="text-xl font-semibold text-foreground md:text-center">
          {t.crmTeamSizeTitle}
        </h2>
      </div>

      <div className="space-y-2.5 mb-4">
        {TEAM_SIZE_OPTIONS.map((opt) => {
          const isSelected = selected === opt.id;
          return (
            <button
              key={opt.id}
              type="button"
              onClick={() => handleSelect(opt.id)}
              className={cn(
                'w-full flex items-center justify-between px-4 py-3 rounded-md border transition-colors',
                isSelected
                  ? 'border-primary bg-primary/10'
                  : 'border-border hover:bg-muted'
              )}
            >
              <div className="flex items-center gap-3">
                <span className="text-lg">{opt.icon}</span>
                <span className="font-medium text-sm text-foreground">{t[opt.labelKey]}</span>
              </div>
              {isSelected && <Check className="w-5 h-5 text-primary" />}
            </button>
          );
        })}
      </div>

      <div className="sticky bottom-0 pt-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] bg-gradient-to-t from-background via-background to-transparent">
        <Button onClick={onNext} className="w-full h-12 rounded-md text-base" disabled={!selected}>
          {t.next}
        </Button>
      </div>
    </div>
  );
}
